import type { LinkSource, LinkTarget } from "@nabble/sdk";
import { driveApi } from "../api/drive";
import type { DriveNode, Listing } from "../api/types";
import { kindLabel } from "../model/files";

/** Stable addresses other modules may link to (docs/modules/drive.md). */
export const nodeUrl = (node: Pick<DriveNode, "id" | "kind">): string =>
  node.kind === "FOLDER" ? `/drive/folders/${node.id}` : `/drive/files/${node.id}`;

export function linkTargetOf(node: DriveNode, hint?: string): LinkTarget {
  const folder = node.kind === "FOLDER";
  return { id: node.id, title: node.name, url: nodeUrl(node), kind: folder ? "Folder" : kindLabel(node.name), container: folder, ...(hint ? { hint } : {}) };
}

const ready = (nodes: DriveNode[], hint?: string): LinkTarget[] =>
  nodes.filter((n) => n.status !== "UPLOADING").map((n) => linkTargetOf(n, hint));

/** The folder reached by following the names from the top of the first drive (ignoring case). */
async function listingAt(path: string[], signal: AbortSignal): Promise<Listing> {
  const [first] = await driveApi.spaces();
  if (!first) throw new Error("no drive");
  let listing = await driveApi.root(first.organizationId);
  for (const name of path) {
    if (signal.aborted) throw new Error("aborted");
    const folder = listing.children.find((c) => c.kind === "FOLDER" && c.name.toLowerCase() === name.toLowerCase());
    if (!folder) throw new Error("not found");
    listing = await driveApi.folder(folder.id);
  }
  return listing;
}

/**
 * Documents Drive files and folders, for other modules to link to through the shell (docs/adr/0021):
 * a name search, or browsing folder by folder from the top of the first drive.
 */
export const driveLinkSource: LinkSource = {
  id: "drive",
  label: "Documents Drive",
  icon: "file",
  search: async (query, signal) => (query ? ready(await driveApi.search(query, signal)) : ready((await listingAt([], signal)).children)),
  browse: async (path, signal) => ready((await listingAt(path, signal)).children, path.length > 0 ? path.join(" / ") : undefined),
};
